import { useNavigate, useSearch } from '@tanstack/react-router'
import { getISODay, parseISO } from 'date-fns'
import { ArrowLeft, ChevronRight, History, Maximize2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { DayList } from '@/components/timesheet/DayList'
import { DayView } from '@/components/timesheet/DayView'
import { PersonSwitcher } from '@/components/timesheet/PersonSwitcher'
import { TeamDayView } from '@/components/timesheet/TeamDayView'
import { UtilizationBar } from '@/components/timesheet/UtilizationBar'
import { WeekGrid } from '@/components/timesheet/WeekGrid'
import { WeekHistoryDrawer } from '@/components/timesheet/WeekHistoryDrawer'
import { TimesheetToolbar } from '@/components/timesheet/TimesheetToolbar'
import { WeekNav, WeekTotalsStrip } from '@/components/timesheet/WeekNav'
import { StoplichtDot } from '@/components/timesheet/StoplichtDot'
import { ApprovalGlyph, TeamApproveButton } from '@/components/timesheet/TeamApproval'
import { useTimesheetRows } from '@/components/timesheet/useTimesheetRows'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Skeleton } from '@/components/ui/Skeleton'
import { PageHeader } from '@/components/ui/PageHeader'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { toNumber } from '@/lib/decimal'
import { formatContractHours } from '@/lib/format'
import { formatHmm } from '@/lib/hours'
import { useMe, useTeamPersonNames, useTeamWeek, useTeamWeekTotals, useWeek } from '@/lib/time-queries'
import type { TeamPersonWeek } from '@/lib/types'
import { useIsCompact } from '@/lib/use-media-query'
import { cn } from '@/lib/utils'
import { rememberTeamView, type TeamView } from '@/lib/team-view'
import { dayStatus, isoWeekId, todayIsoDate, weekMetaFromId, type DayMeta } from '@/lib/week'

function utilizationTooltip(person: TeamPersonWeek): string {
  const util = person.utilization
  if (!util) return 'Flexibel of declarabiliteit uit'
  const parts: string[] = []
  if (util.percentage != null) parts.push(`${util.percentage}% declarabel`)
  if (util.expectedHours != null) parts.push(`verwacht ${formatHmm(toNumber(util.expectedHours))} u`)
  if (util.contractHours != null) parts.push(`${formatContractHours(toNumber(util.contractHours))} u contract`)
  const vacation = person.dailyTotals.filter((d) => d.dayType === 'vacation').length
  const holiday = person.dailyTotals.filter((d) => d.dayType === 'holiday').length
  if (vacation > 0) parts.push(`${vacation} vakantiedag${vacation === 1 ? '' : 'en'}`)
  if (holiday > 0) parts.push(`${holiday} feestdag${holiday === 1 ? '' : 'en'}`)
  return parts.join(' · ')
}

function TeamUtilizationCell({ person }: { person: TeamPersonWeek }) {
  const util = person.utilization
  if (!util) {
    return <span className="money text-ink-faint" title="Flexibel of declarabiliteit uit">&#x2013;</span>
  }
  return (
    <UtilizationBar
      hours={util.hours}
      contractHours={util.contractHours}
      percentage={util.percentage}
      align="right"
      contractTitle={utilizationTooltip(person)}
      className="min-w-[84px]"
    />
  )
}

export function TimesheetTeamPage() {
  const navigate = useNavigate()
  const meQuery = useMe()
  const search = useSearch({ strict: false }) as { person?: string; week?: string; focus?: boolean; date?: string }
  const [thisWeek] = useState(() => isoWeekId(new Date()))
  // De aanwezigheid van date bepaalt de weergave; een aparte view-parameter zou een link
  // kunnen opleveren die zichzelf tegenspreekt.
  const dayDate = search.date
  // Staat er een dag, dan wint die van een meegereisde week: de week eromheen leidt uit
  // de dag af. Andersom zou een weekstap alleen de dag verzetten terwijl de week bleef
  // staan, en dan bevriest de weekstepper op de week die toevallig in de URL stond.
  const weekId = dayDate ? isoWeekId(parseISO(dayDate)) : (search.week ?? thisWeek)
  const personId = search.person
  const [historyOpen, setHistoryOpen] = useState(false)
  const compact = useIsCompact()
  const teamQuery = useTeamWeek(weekId, dayDate === undefined || search.focus === true)
  const meta = weekMetaFromId(weekId)

  const isAdmin = meQuery.data?.user.role === 'ADMIN'
  const persons = teamQuery.data?.persons ?? []
  const inList = persons.find((person) => person.personId === personId)

  /**
   * useTeamWeek heeft geen placeholder, dus bij elke weekstap is de lijst even leeg
   * en zou de persoonstepper doodvallen. De laatste gevulde lijst blijft daarom
   * staan als keuzevoorraad; de tabel zelf toont wel gewoon de laadstaat.
   */
  const [lastPersons, setLastPersons] = useState<TeamPersonWeek[]>([])
  useEffect(() => {
    if (persons.length > 0) setLastPersons(persons)
  }, [persons])

  // In het teamdagoverzicht markeert een persoon alleen een rij, maar in focus draagt
  // hij de kop, dus daar moet de naam er zijn ook als het weekrooster niet meeloopt.
  const namesQuery = useTeamPersonNames(personId !== undefined && (dayDate === undefined || search.focus === true))
  const knownName =
    inList?.displayName ??
    lastPersons.find((person) => person.personId === personId)?.displayName ??
    (personId !== undefined ? namesQuery.data?.get(personId) : undefined) ??
    null
  const selected = personId !== undefined && knownName !== null ? { id: personId, name: knownName } : null
  // Pas onbekend als de server alle namen heeft geleverd en die van deze link er niet bij
  // zit; een mislukte of lopende query zegt niets over de persoon.
  const unknownPerson = personId !== undefined && knownName === null && namesQuery.isSuccess
  const focus = search.focus === true && selected !== null

  const teamTotalsQuery = useTeamWeekTotals(weekId, !focus && dayDate === undefined)
  const selectedWeekQuery = useWeek(weekId, selected?.id, selected !== null)
  const selectedRows = useTimesheetRows(weekId, selected ? selectedWeekQuery.data?.rows ?? [] : [], selected?.id)
  const weekendActive = persons.some((person) =>
    person.dailyTotals.some((d) => !d.isWorkday && toNumber(d.total) > 0),
  )
  const visibleDays = meta.days.filter((d) => d.isWorkday || weekendActive)

  /**
   * Bladeren binnen hetzelfde scherm vervangt de historie-stap: anders kost een week
   * terugbladeren evenveel drukken op terug. Een wissel die het hele scherm verandert
   * (dag <-> week, doorklikken naar iemands week) zet juist wel een stap, zodat de
   * browserknop terugbrengt waar je vandaan kwam.
   */
  function setSearch(patch: { person?: string; week?: string; focus?: boolean; date?: string }, push = false) {
    navigate({ to: '/uren/team', search: (prev) => ({ ...prev, ...patch }), replace: !push })
  }

  function selectPerson(person: TeamPersonWeek) {
    setSearch({ person: person.personId })
  }

  /**
   * Onder sm staat er geen personentabel en dus ook geen ingebed grid: daar is een teamlid
   * een eigen scherm. De bestemming hangt aan het element dat je aantikt en niet aan een
   * mediaquery in JS, want de kaartenlijst en de tabel zijn al gescheiden DOM; een tweede
   * bron van waarheid zou stil uit de pas kunnen lopen met de CSS-grens.
   */
  function openPersonScreen(person: TeamPersonWeek) {
    setSearch({ person: person.personId, focus: true }, true)
  }

  // De week wisselen laat de gekozen persoon staan; juist doorbladeren door de
  // tijd van een teamlid was de reden om hem aan te klikken.
  function changeWeek(id: string) {
    setSearch({ week: id })
  }

  /**
   * De dag binnen de focusstand. Staat hij in de URL, dan is dat de dag; anders valt
   * hij terug op vandaag, en op maandag zodra vandaag buiten de getoonde week ligt.
   * De mobiele daglijst leest dezelfde waarde, zodat dag- en weekweergave op elke
   * breedte over dezelfde dag gaan.
   */
  const todayInWeek = meta.days.find((day) => day.iso === todayIsoDate())
  const focusDayIndex = dayDate ? getISODay(parseISO(dayDate)) - 1 : (todayInWeek?.dayIndex ?? 0)

  /**
   * Week en dag komen als paar binnen en gaan als een datum de URL in. De week gaat er
   * expliciet uit: hij is af te leiden uit de dag, en laten staan zou een tweede,
   * tegensprekende bron in de link achterlaten.
   */
  function focusGoToDay(week: string, day: number) {
    setSearch({ date: weekMetaFromId(week).days[day].iso, week: undefined })
  }

  // De persoon en de focusstand blijven bij een weergavewissel staan: je kijkt naar
  // dezelfde iemand, alleen met een andere periode eromheen.
  function showDay() {
    const today = todayIsoDate()
    const inWeek = meta.days.some((day) => day.iso === today)
    rememberTeamView('day')
    setSearch({ date: inWeek ? today : meta.days[0].iso, week: undefined }, true)
  }

  function showWeek() {
    rememberTeamView('week')
    setSearch({ week: weekId, date: undefined }, true)
  }

  const viewControl = (
    <SegmentedControl<TeamView>
      segments={[
        { value: 'day', label: 'Dag' },
        { value: 'week', label: 'Week' },
      ]}
      value={dayDate === undefined ? 'week' : 'day'}
      onChange={(next) => {
        if (next === 'day' && dayDate === undefined) showDay()
        if (next === 'week' && dayDate !== undefined) showWeek()
      }}
      label="Weergave"
    />
  )

  const segmented = (
    <SegmentedControl<'mine' | 'team'>
      segments={[
        { value: 'mine', label: 'Mijn uren' },
        { value: 'team', label: 'Team' },
      ]}
      value="team"
      onChange={(value) => {
        if (value === 'mine') navigate({ to: '/uren' })
      }}
    />
  )

  // Historie mag ook een Partner van collega's zien; alleen muteren blijft Beheerder.
  const historyButton =
    selected ? (
      <button type="button" onClick={() => setHistoryOpen(true)} className="btn-secondary h-8 px-2.5 text-sm">
        <History className="h-4 w-4" />
        Historie
      </button>
    ) : null

  /**
   * In focus draait alles om een periode van een persoon, dus levert WeekGrid of
   * DayView zelf de balk: navigatie en persoonstepper links, periode en grafiek in
   * het midden, de acties rechts. Een tweede balk van deze pagina zou het
   * doen-cluster in een aparte regel duwen.
   */
  if (focus && selected) {
    const personControl = (
      <>
        <PersonSwitcher
          persons={lastPersons}
          personId={selected.id}
          personName={selected.name}
          onChange={(id) => setSearch({ person: id })}
        />
        {viewControl}
      </>
    )

    return (
      <div className="space-y-6">
        <div>
          <button
            type="button"
            onClick={() => setSearch({ focus: undefined })}
            className="mb-2 -ml-1 inline-flex items-center gap-1.5 rounded-sm px-1 py-1.5 text-sm text-ink-faint hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4" />
            Teamoverzicht
          </button>
          <PageHeader title={`Uren van ${selected.name}`} actions={segmented} />
        </div>

        {/* Zelfde tweedeling als de eigen urenpagina: een grid van 640px is op een
            telefoon geen invoerscherm, dus daar draait de daglijst. */}
        <div className="hidden md:block">
          {dayDate !== undefined ? (
            <DayView
              weekId={weekId}
              dayIndex={focusDayIndex}
              onNavigate={focusGoToDay}
              personId={selected.id}
              me={meQuery.data}
              editable={isAdmin}
              sheet={selectedRows}
              viewControl={personControl}
              actions={historyButton}
            />
          ) : (
            <WeekGrid
              weekId={weekId}
              onWeekChange={changeWeek}
              personId={selected.id}
              me={meQuery.data}
              editable={isAdmin}
              sheet={selectedRows}
              viewControl={personControl}
              actions={historyButton}
            />
          )}
        </div>
        <div className="md:hidden">
          <DayList
            weekId={weekId}
            dayIndex={focusDayIndex}
            onNavigate={focusGoToDay}
            personId={selected.id}
            me={meQuery.data}
            editable={isAdmin}
            sheet={selectedRows}
          />
        </div>

        <WeekHistoryDrawer
          weekId={weekId}
          personId={selected.id}
          personName={selected.name}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      </div>
    )
  }

  /**
   * Het teamdagoverzicht is alleen-lezen: er staat geen grid en geen historie-lade
   * onder, want elk pad naar bewerken loopt via de periode van die ene persoon.
   */
  if (dayDate !== undefined) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Team-dagoverzicht"
          description="Wie boekte wat op deze dag. Klik door naar iemands week."
          actions={segmented}
        />

        <TeamDayView
          date={dayDate}
          onDateChange={(next) => setSearch({ date: next })}
          // De gatenstrip in de dagweergave staat op elke breedte in dezelfde DOM, dus
          // hier kan de bestemming niet uit een splitsing komen en moet hij aan de breedte
          // gevraagd worden. focus gaat expliciet mee, ook als undefined: dat ruimt een
          // blijven hangende focus uit een eerdere link meteen op.
          onOpenPersonWeek={(id) =>
            setSearch(
              {
                person: id,
                week: isoWeekId(parseISO(dayDate)),
                date: undefined,
                focus: compact ? true : undefined,
              },
              true,
            )
          }
          highlightPersonId={personId}
          viewControl={viewControl}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team-weekoverzicht"
        description="Personen tegen dagen. Klik door naar iemands week."
        actions={segmented}
      />

      <TimesheetToolbar
        nav={
          <>
            <WeekNav weekId={weekId} onChange={changeWeek} />
            {viewControl}
          </>
        }
        readout={
          <>
            {/* Wijkt als eerste bij krappe breedte, net als in WeekGrid: het weeknummer
                en de kalender benoemen de periode al. */}
            <span className="hidden text-sm text-ink-faint min-[1400px]:inline">{meta.rangeShort}</span>
            {/* Zie TeamDayView: naamloze staafjes van 12px zijn op een telefoon geen
                grafiek maar ruis, en de weekstepper doet daar de navigatie. */}
            {teamTotalsQuery.data?.weeks?.length ? (
              <div className="hidden sm:block">
                <WeekTotalsStrip totals={teamTotalsQuery.data.weeks} weekId={weekId} onChange={changeWeek} />
              </div>
            ) : null}
          </>
        }
      />

      {unknownPerson ? (
        <EmptyState
          title="Deze persoon staat niet in dit overzicht"
          description="De link verwijst naar iemand die deze week niet in het teamoverzicht voorkomt."
          action={
            <button type="button" className="btn-secondary" onClick={() => setSearch({ person: undefined, focus: undefined })}>
              Terug naar team
            </button>
          }
        />
      ) : null}

      <div className="hidden sm:block">
        <div className="overflow-hidden rounded-card bg-surface shadow-card">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-hairline text-ink-faint">
                  <th className="sticky left-0 z-10 bg-surface px-4 py-3 text-left font-medium">Persoon</th>
                  {visibleDays.map((day) => (
                    <th key={day.dayIndex} className="px-2 py-3 text-center font-medium">
                      <div>{day.short}</div>
                      <div className="num text-xs font-normal text-ink-faint/70">{day.date.getDate()}</div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right font-medium">Week</th>
                  <th className="px-4 py-3 text-right font-medium">Declarabel</th>
                </tr>
              </thead>
              <tbody>
                {teamQuery.isError ? (
                  <tr>
                    <td colSpan={visibleDays.length + 3}>
                      <ErrorState compact error={teamQuery.error} onRetry={() => teamQuery.refetch()} />
                    </td>
                  </tr>
                ) : teamQuery.isLoading ? (
                  Array.from({ length: 3 }).map((_, r) => (
                    <tr key={r} className="border-b border-hairline">
                      <td className="px-4 py-3">
                        <Skeleton className="h-4 w-36" />
                      </td>
                      {visibleDays.map((day) => (
                        <td key={day.dayIndex} className="px-2 py-3">
                          <Skeleton className="mx-auto h-4 w-8" />
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <Skeleton className="ml-auto h-4 w-8" />
                      </td>
                      <td className="px-4 py-3">
                        <Skeleton className="ml-auto h-4 w-16" />
                      </td>
                    </tr>
                  ))
                ) : (
                  persons.map((person) => {
                    const byDay = new Map(person.dailyTotals.map((d) => [d.dayIndex, d]))
                    const active = selected?.id === person.personId
                    return (
                      <tr
                        key={person.personId}
                        onClick={() => selectPerson(person)}
                        className={cn(
                          'group cursor-pointer border-b border-hairline transition-colors hover:bg-fill-1',
                          active && 'bg-accent-600/5',
                        )}
                      >
                        <td
                          className={cn(
                            'sticky left-0 z-10 px-4 py-3 group-hover:[background:linear-gradient(var(--fill-1),var(--fill-1)),rgb(var(--surface-rgb))]',
                            active
                              ? '[background:linear-gradient(rgb(var(--accent-600-rgb)/0.05),rgb(var(--accent-600-rgb)/0.05)),rgb(var(--surface-rgb))]'
                              : 'bg-surface',
                          )}
                        >
                          <span className="inline-flex items-center gap-1.5 font-medium text-ink">
                            <ApprovalGlyph state={person.approval.state} />
                            {person.displayName}
                            <ChevronRight className="h-3.5 w-3.5 text-ink-faint" />
                            {isAdmin ? (
                              <TeamApproveButton weekId={weekId} personId={person.personId} state={person.approval.state} />
                            ) : null}
                          </span>
                        </td>
                        {visibleDays.map((day) => {
                          const total = toNumber(byDay.get(day.dayIndex)?.total)
                          return (
                            <td key={day.dayIndex} className="px-2 py-3 text-center">
                              <div className={cn('num', total > 0 ? 'text-ink' : 'text-ink-faint/50')}>
                                {formatHmm(total)}
                              </div>
                              <div className="mt-1.5 flex justify-center">
                                <StoplichtDot status={dayStatus(total, day.isWorkday)} />
                              </div>
                            </td>
                          )
                        })}
                        <td className="num px-4 py-3 text-right font-semibold text-ink">
                          {formatHmm(toNumber(person.weekTotal))}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end">
                            <TeamUtilizationCell person={person} />
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="sm:hidden">
        {teamQuery.isError ? (
          <ErrorState compact error={teamQuery.error} onRetry={() => teamQuery.refetch()} />
        ) : teamQuery.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full rounded-card" />
            <Skeleton className="h-16 w-full rounded-card" />
            <Skeleton className="h-16 w-full rounded-card" />
          </div>
        ) : (
          <ul className="space-y-2">
            {persons.map((person) => (
              <MobilePersonCard
                key={person.personId}
                person={person}
                days={visibleDays}
                active={selected?.id === person.personId}
                onOpen={() => openPersonScreen(person)}
              />
            ))}
          </ul>
        )}
      </div>

      {selected ? (
        // Het ingebedde grid is het detail van de personentabel en deelt dus haar
        // breakpoint, niet de md-grens van de grid/daglijst-tweedeling: wie tussen sm en
        // md een rij aanklikt moet er wel iets onder krijgen. Onder sm bestaat de tabel
        // niet en is een teamlid een eigen scherm.
        <div className="hidden space-y-3 sm:block">
          <WeekGrid
            weekId={weekId}
            onWeekChange={changeWeek}
            personId={selected.id}
            me={meQuery.data}
            editable={isAdmin}
            sheet={selectedRows}
            embeddedHeading={
              <>
                <h2 className="text-sm font-semibold text-ink">Week van {selected.name}</h2>
                {/* Dat je bewerkt is geen nieuws; de cellen zijn zichtbaar bewerkbaar en een
                    afgesloten week zegt dat zelf al. Alleen-lezen is dat wel. */}
                {isAdmin ? null : <span className="text-sm font-normal text-ink-faint">alleen-lezen</span>}
                <button
                  type="button"
                  onClick={() => setSearch({ focus: true })}
                  className="btn-secondary h-8 w-8 p-0"
                  aria-label="Alleen deze persoon tonen"
                  title="Alleen deze persoon tonen"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
                {historyButton}
              </>
            }
          />
        </div>
      ) : null}

      {/* Buiten de breakpoint-wrapper: de lade is een fixed overlay zonder portal, en die
          hoort niet van een display:none-voorouder af te hangen. */}
      {selected ? (
        <WeekHistoryDrawer
          weekId={weekId}
          personId={selected.id}
          personName={selected.name}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      ) : null}
    </div>
  )
}

interface MobilePersonCardProps {
  person: TeamPersonWeek
  days: DayMeta[]
  active: boolean
  onOpen: () => void
}

function MobilePersonCard({ person, days, active, onOpen }: MobilePersonCardProps) {
  const byDay = new Map(person.dailyTotals.map((d) => [d.dayIndex, d]))

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        // Na een terugstap blijft de persoon in de URL staan en licht zijn kaart op; met
        // aria-current komt dat ook over zonder de kleur te zien.
        aria-current={active ? true : undefined}
        className={cn(
          'w-full rounded-card border border-hairline bg-surface px-4 py-3 text-left shadow-card transition-colors',
          active && 'border-accent-600/40 bg-accent-600/5',
        )}
      >
        <span className="flex items-center justify-between gap-3">
          <span className="inline-flex min-w-0 items-center gap-1.5 font-medium text-ink">
            <ApprovalGlyph state={person.approval.state} className="shrink-0" />
            <span className="truncate">{person.displayName}</span>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-faint" />
          </span>
          <span className="num shrink-0 text-sm font-semibold text-ink">
            {formatHmm(toNumber(person.weekTotal))}
          </span>
        </span>
        <span className="mt-2 flex items-center gap-2.5">
          {days.map((day) => {
            const info = byDay.get(day.dayIndex)
            const total = toNumber(info?.total)
            return (
              <span key={day.dayIndex} className="flex flex-col items-center gap-1">
                <span className="text-[10px] uppercase tracking-wide text-ink-faint">{day.short}</span>
                <StoplichtDot status={dayStatus(total, info?.expectedWorkday ?? day.isWorkday)} />
              </span>
            )
          })}
        </span>
        {person.utilization ? (
          <span className="mt-3 flex items-center justify-between gap-3 border-t border-hairline pt-2.5">
            <span className="label-caps">Declarabel</span>
            <UtilizationBar
              hours={person.utilization.hours}
              contractHours={person.utilization.contractHours}
              percentage={person.utilization.percentage}
              align="right"
              className="min-w-[96px]"
            />
          </span>
        ) : null}
      </button>
    </li>
  )
}
